import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Time, SeriesMarker } from 'lightweight-charts';
import { useSetCurrentPage, useAppStore } from '../stores/useAppStore';
import { KlineCandle } from '../services/klineTypes';
import { BarInterval, POSITION_SIDE } from '@/services/enums';
import { backtestService, BacktestResult, BacktestTrade } from '../services/backtestService';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import KlineChart, { KlineChartRef } from '../components/KlineChart';
import KlinePlaybackControls from '../components/KlinePlaybackControls';
import KlineAIPromptBuilder from '../components/KlineAIPromptBuilder';
import KlineMetricsDisplay from '../components/KlineMetricsDisplay';
import PageHeader from '../components/PageHeader';
import { ThemeMode } from '@/services/enums';

interface KlinePlaybackProps {
  symbol?: string;
  instType?: string;
}

const KlinePlayback: React.FC<KlinePlaybackProps> = ({ symbol: initialSymbol, instType: _initialInstType }) => {
  const chartRef = useRef<KlineChartRef>(null);
  const playbackTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const setCurrentPage = useSetCurrentPage();
  const setPlaybackRange = useAppStore((state) => state.setPlaybackRange);
  const playbackRange = useAppStore((state) => state.playbackRange);
  const playbackSymbolState = useAppStore((state) => state.playbackSymbol);
  const [symbol, setSymbol] = useState(initialSymbol || '');
  const symbolRef = useRef<string>(symbol);
  const [bar, setBar] = useState<BarInterval>('4H');
  const barRef = useRef<BarInterval>(bar);
  const lastBarSymbolRef = useRef<string>(''); // 跟踪已处理过的 bar+symbol 组合，防止重复清空
  const [visibleRange, setVisibleRange] = useState<{ from: number; to: number } | null>(null);

  const handleVisibleRangeChange = useCallback(
    ({ from, to }: { from: number; to: number }) => {
      setVisibleRange({ from, to });
      setPlaybackRange({ from, to, bar });
    },
    [bar, setPlaybackRange],
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1); // 1x, 0.25x, 0.5x, 2x, 4x
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allCandles, setAllCandles] = useState<KlineCandle[]>([]);
  const [displayedCandles, setDisplayedCandles] = useState<KlineCandle[]>([]);
  const [loading, setLoading] = useState(false); // 用于控制KlineChart的loading状态
  const [error, setError] = useState<string | null>(null);
  const theme: ThemeMode = 'dark';
  const showVolume = true;
  const autoScroll = true;
  const [backtestList, setBacktestList] = useState<BacktestResult[]>([]); // 回测记录列表
  const [selectedBacktestId, setSelectedBacktestId] = useState<number | null>(null); // 选中的回测记录
  const [backtestTrades, setBacktestTrades] = useState<BacktestTrade[]>([]); // 回测交易记录

  // 初始加载时从URL或store获取参数
  useEffect(() => {
    // 可以从 URL 查询参数获取 symbol，或从全局状态获取
    const params = new URLSearchParams(window.location.search);
    const urlSymbol = params.get('symbol');
    if (urlSymbol) {
      setSymbol(urlSymbol);
    } else if (useAppStore.getState().playbackSymbol) {
      setSymbol(useAppStore.getState().playbackSymbol);
    }
  }, []);

  // 加载回测记录列表
  const backtestListLoadedRef = useRef(false);
  useEffect(() => {
    if (backtestListLoadedRef.current || !symbol) return;
    backtestListLoadedRef.current = true;

    const loadBacktestList = async () => {
      try {
        const response = await backtestService.getBacktestList({
          page: 1,
          pageSize: 50,
          status: 'completed',
          symbol: symbol,
        });
        setBacktestList(response.data || []);
      } catch (err) {
        console.error('加载回测列表失败:', err);
      }
    };
    loadBacktestList();
  }, [symbol]);

  // 当选择回测记录时，加载交易记录
  useEffect(() => {
    if (!selectedBacktestId) {
      setBacktestTrades([]);
      return;
    }
    const loadTrades = async () => {
      try {
        const response = await backtestService.getBacktestTrades(selectedBacktestId, { page: 1, pageSize: 1000 });
        setBacktestTrades(response.data || []);
      } catch (err) {
        console.error('加载回测交易记录失败:', err);
        setBacktestTrades([]);
      }
    };
    loadTrades();
  }, [selectedBacktestId]);

  // 根据回测交易记录生成买卖信号标记
  const calculateSignalMarkers = useCallback((trades: BacktestTrade[]): SeriesMarker<Time>[] => {
    const markers: SeriesMarker<Time>[] = [];

    for (const trade of trades) {
      // 入场信号
      const entryTime = Math.floor(new Date(trade.entryTime).getTime() / 1000) as Time;
      const isLong = trade.positionSide === POSITION_SIDE.LONG;
      const entryPriceNum = Number(trade.entryPrice);
      const entryPriceText = Number.isFinite(entryPriceNum) ? entryPriceNum.toFixed(4) : '-';

      markers.push({
        time: entryTime,
        position: isLong ? 'belowBar' : 'aboveBar',
        color: isLong ? '#26a69a' : '#ef5350',
        shape: isLong ? 'arrowUp' : 'arrowDown',
        text: `${isLong ? '买' : '卖'} ${entryPriceText}`,
      });

      // 出场信号
      if (trade.exitTime && trade.status === 'closed') {
        const exitTime = Math.floor(new Date(trade.exitTime).getTime() / 1000) as Time;
        const exitPriceNum = Number(trade.exitPrice);
        const exitPriceText = Number.isFinite(exitPriceNum) ? exitPriceNum.toFixed(4) : '-';
        markers.push({
          time: exitTime,
          position: isLong ? 'aboveBar' : 'belowBar',
          color: isLong ? '#ef5350' : '#26a69a',
          shape: isLong ? 'arrowDown' : 'arrowUp',
          text: `平 ${exitPriceText}`,
        });
      }
    }

    // 按时间排序
    markers.sort((a, b) => (a.time as number) - (b.time as number));

    return markers;
  }, []);

  // 计算回测交易标记
  const signalMarkers = useMemo(() => {
    if (!selectedBacktestId || backtestTrades.length === 0) {
      return [];
    }
    return calculateSignalMarkers(backtestTrades);
  }, [backtestTrades, selectedBacktestId, calculateSignalMarkers]);

  // K线数据加载完成回调
  const handleKlineLoad = useCallback(
    (candles: KlineCandle[]) => {
      console.log('[KlinePlayback] K线数据加载完成', { count: candles.length });
      setLoading(false);
      if (candles.length === 0) {
        setAllCandles([]);
        setDisplayedCandles([]);
        setCurrentIndex(0);
        return;
      }

      setAllCandles(candles);

      // 初始化显示所有K线，进度为100%
      setDisplayedCandles(candles);
      setCurrentIndex(candles.length - 1); // 初始索引为最后一条，进度为100%

      // 尝试从缓存恢复视口范围
      if (playbackSymbolState === symbol && playbackRange && playbackRange.bar === bar && chartRef.current) {
        console.log('[KlinePlayback] 恢复视口范围:', playbackRange);
        // 使用 setTimeout 确保图表已经渲染了数据
        setTimeout(() => {
          if (chartRef.current) {
            chartRef.current.setVisibleRange({
              from: playbackRange.from,
              to: playbackRange.to,
            });
          }
        }, 100);
      }
    },
    [symbol, bar, playbackRange, playbackSymbolState],
  );

  // K线数据加载错误回调
  const handleKlineError = useCallback((errorMsg: string) => {
    setLoading(false);
    setError(errorMsg);
    setAllCandles([]);
    setDisplayedCandles([]);
  }, []);

  // 当symbol或bar变化时，设置loading状态
  useEffect(() => {
    if (symbol && displayedCandles.length === 0) {
      setLoading(true);
    }
  }, [symbol, bar, displayedCandles.length]);

  // 更新 ref，确保监听器能访问到最新的值
  useEffect(() => {
    barRef.current = bar;
  }, [bar]);

  useEffect(() => {
    symbolRef.current = symbol;
  }, [symbol]);

  // 当symbol或bar变化时重置播放状态
  useEffect(() => {
    if (symbol) {
      setIsPlaying(false);
    }
  }, [symbol, bar]);

  // 当bar变化时，清空K线数据并触发重新加载
  useEffect(() => {
    if (!symbol || !bar) {
      return;
    }

    // 生成当前 bar+symbol 的唯一标识
    const currentKey = `${symbol}_${bar}`;

    // 防止重复清空：如果已经处理过相同的 bar+symbol 组合，则跳过
    if (lastBarSymbolRef.current === currentKey) {
      return;
    }

    // 如果是首次加载（lastBarSymbolRef.current 为空），只标记不清空数据
    const isInitialLoad = lastBarSymbolRef.current === '';

    // 标记为已处理
    lastBarSymbolRef.current = currentKey;

    // 首次加载时不清空数据，只标记已处理
    if (isInitialLoad) {
      return;
    }

    // 清空所有K线数据
    setAllCandles([]);
    setDisplayedCandles([]);
    setCurrentIndex(0);
    setError(null);
    setLoading(true);

    // 停止播放
    setIsPlaying(false);

    console.log('[KlinePlayback] 时间粒度变化，清空数据并重新加载', { symbol, bar });
  }, [bar, symbol]);

  // 注意：图表数据现在由KlineChart组件内部管理，不再需要手动调用setData

  // 播放控制
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    if (allCandles.length === 0 || currentIndex >= allCandles.length - 1) {
      setIsPlaying(false);
      return;
    }

    // 速度映射：1x = 200ms, 0.25x = 800ms, 0.5x = 400ms, 2x = 100ms, 4x = 50ms
    const speedMap: Record<number, number> = {
      0.25: 800,
      0.5: 400,
      1: 200,
      2: 100,
      4: 50,
    };
    const interval = speedMap[speed] || 200;

    let lastTime = Date.now();
    let accumulatedTime = 0;
    let shouldContinue = true;

    const tick = () => {
      if (!shouldContinue) {
        return;
      }

      const now = Date.now();
      const delta = now - lastTime;
      lastTime = now;
      accumulatedTime += delta;

      if (accumulatedTime >= interval) {
        accumulatedTime = 0;

        // 检查是否到达末尾
        setCurrentIndex((prevIndex) => {
          if (prevIndex >= allCandles.length - 1) {
            setIsPlaying(false);
            return prevIndex;
          }

          const nextIndex = prevIndex + 1;
          const nextCandleData = allCandles[nextIndex];

          // 增量更新K线
          if (chartRef.current) {
            chartRef.current.updateCandle(nextCandleData);
          }

          // 更新显示的K线数组
          setDisplayedCandles((prev) => {
            const newCandles = [...prev];
            if (newCandles.length > 0 && newCandles[newCandles.length - 1].time === nextCandleData.time) {
              newCandles[newCandles.length - 1] = nextCandleData;
            } else {
              newCandles.push(nextCandleData);
            }
            return newCandles;
          });

          // 自动滚动
          if (autoScroll && chartRef.current) {
            chartRef.current.scrollToRealTime();
          }

          return nextIndex;
        });
      }

      if (shouldContinue) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    const frameId = requestAnimationFrame(tick);
    rafRef.current = frameId;

    return () => {
      shouldContinue = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, speed, allCandles, showVolume, autoScroll, currentIndex]);

  const playPause = useCallback(() => {
    if (allCandles.length === 0) return;

    if (isPlaying) {
      // 暂停
      setIsPlaying(false);
    } else {
      // 播放
      if (currentIndex >= allCandles.length - 1) {
        // 如果已经播放到末尾，重新开始
        setCurrentIndex(0);
        setDisplayedCandles(allCandles.slice(0, Math.min(100, allCandles.length)));
      }
      setIsPlaying(true);
    }
  }, [allCandles, currentIndex, isPlaying]);

  const nextCandle = useCallback(() => {
    if (currentIndex >= allCandles.length - 1) {
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);

    // 增量更新：只添加新的K线
    const nextCandleData = allCandles[nextIndex];
    if (chartRef.current) {
      chartRef.current.updateCandle(nextCandleData);
    }

    // 更新显示的K线数组
    setDisplayedCandles((prev) => {
      const newCandles = [...prev];
      if (newCandles.length > 0 && newCandles[newCandles.length - 1].time === nextCandleData.time) {
        // 替换最后一根
        newCandles[newCandles.length - 1] = nextCandleData;
      } else {
        // 添加新的
        newCandles.push(nextCandleData);
      }
      return newCandles;
    });

    // 自动滚动
    if (autoScroll && chartRef.current) {
      chartRef.current.scrollToRealTime();
    }
  }, [currentIndex, allCandles, autoScroll]);

  const prevCandle = useCallback(() => {
    if (currentIndex <= 0) return;
    const prevIndex = currentIndex - 1;
    setCurrentIndex(prevIndex);
    const prevCandles = allCandles.slice(0, prevIndex + 1);
    setDisplayedCandles(prevCandles);
    if (chartRef.current) {
      chartRef.current.setData(prevCandles);
    }
  }, [currentIndex, allCandles]);

  const stop = () => {
    setIsPlaying(false);
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setCurrentIndex(0);
    setDisplayedCandles(allCandles.slice(0, Math.min(100, allCandles.length)));
  };

  const handleProgressChange = (progress: number) => {
    if (allCandles.length === 0) return;
    const targetIndex = Math.floor((progress / 100) * (allCandles.length - 1));
    setCurrentIndex(targetIndex);
    const targetCandles = allCandles.slice(0, targetIndex + 1);
    setDisplayedCandles(targetCandles);
    if (chartRef.current) {
      chartRef.current.setData(targetCandles);
    }
    if (isPlaying) {
      setIsPlaying(false);
    }
  };

  const progress = allCandles.length > 0 ? ((currentIndex + 1) / allCandles.length) * 100 : 0;
  const currentTime =
    displayedCandles.length > 0 ? new Date(displayedCandles[displayedCandles.length - 1].time * 1000) : null;

  // 键盘快捷键
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        playPause();
      } else if (e.key === 'ArrowLeft' && !e.repeat) {
        e.preventDefault();
        prevCandle();
      } else if (e.key === 'ArrowRight' && !e.repeat) {
        e.preventDefault();
        if (!isPlaying) {
          nextCandle();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, playPause, prevCandle, nextCandle]);

  if (!symbol) {
    return (
      <GradientBackground>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-white/60">请先选择币种</div>
        </div>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground>
      <div className="h-screen flex flex-col relative z-10 overflow-hidden">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title={symbol} subtitle="K线回放" onBack={() => setCurrentPage('watchlist')} />

        {/* 错误提示 */}
        {error && (
          <div className="px-4 py-2 bg-red-500/20 border-b border-red-500/50 mt-14">
            <div className="text-red-300 text-sm">{error}</div>
          </div>
        )}

        {/* 图表容器 - 固定高度为屏幕的40% */}
        <div
          className="relative z-20 mt-14 overflow-visible flex-shrink-0"
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <KlineChart
            ref={chartRef}
            symbol={symbol}
            bar={bar}
            theme={theme}
            candles={displayedCandles.length > 0 ? displayedCandles : undefined}
            showVolume={showVolume}
            autoScroll={autoScroll}
            markers={signalMarkers}
            height="35vh"
            onVisibleTimeRangeChange={handleVisibleRangeChange}
            autoLoad={displayedCandles.length === 0}
            loadOptions={{
              end: Math.floor(Date.now() / 1000),
              limit: 200,
            }}
            onLoad={handleKlineLoad}
            onError={handleKlineError}
          />
        </div>

        {/* 可滚动区域 - 控制栏和AI分析 */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* 控制栏 */}
          <KlinePlaybackControls
            bar={bar}
            setBar={setBar}
            speed={speed}
            setSpeed={setSpeed}
            isPlaying={isPlaying}
            playPause={playPause}
            stop={stop}
            prevCandle={prevCandle}
            nextCandle={nextCandle}
            progress={progress}
            handleProgressChange={handleProgressChange}
            currentTime={currentTime}
            allCandles={allCandles}
            currentIndex={currentIndex}
            loading={loading}
            backtestList={backtestList}
            selectedBacktestId={selectedBacktestId}
            setSelectedBacktestId={setSelectedBacktestId}
            visibleRange={visibleRange}
            setPlaybackRange={setPlaybackRange}
            setCurrentPage={setCurrentPage}
          />

          {/* 视口指标分析模块 */}
          <KlineMetricsDisplay symbol={symbol} bar={bar} visibleRange={visibleRange} />

          {/* 分割线 */}
          <div className="border-t border-white/10 my-4"></div>

          {/* 构建AI提示词模块 */}
          <KlineAIPromptBuilder symbol={symbol} bar={bar} visibleRange={visibleRange} />
        </div>
      </div>
    </GradientBackground>
  );
};

export default KlinePlayback;
